import { Component, OnInit, OnDestroy } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment-timezone'
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';
import { ParserService } from "src/app/component/broker-parser/planner-service/parser-service";
import { BrokerService } from 'src/app/service/broker-service/broker.service';
import { LevelOfServiceService } from '../../level-of-service/service/level-of-service.service';
import { Observable, Subject, forkJoin } from 'rxjs';
import { sweetAlert, sweetAlertWithHtml } from 'src/app/utils/swal';
import { TitleCasePipe } from '@angular/common';
import { ServiceAreasService } from '../../service-areas/service-areas-service/service-areas.service';


@Component({
  selector: 'app-marketplace',
  templateUrl: './marketplace.component.html',
  styleUrls: ['./marketplace.component.css']
})
export class MarketplaceComponent implements OnInit, OnDestroy {

  timeZone: any;
  user: any;
  search:any;
  todayDate: any;
  trips:any;
  preLevelOfService = {};
  preSpaceTypes = {};
  preAppointmentType = {};
  preServiceAreas = {};
  cachedAuthData: any = null;

  constructor(
    private sharedDataService: SharedDataService,
    private parserService: ParserService,
    private brokerService: BrokerService,
    private levelOfServiceService: LevelOfServiceService,
    private serviceArea: ServiceAreasService,
  ) { }

  ngOnInit() {
    this.todayDate = [
      moment().startOf('day').toISOString(),
      moment().endOf('day').toISOString()
    ];
    this.getUser()
  }

  getUser() {
    this.sharedDataService
      .getUser()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(user => {
        if (user) {
          this.user = user;
          this.timeZone = this.user.timeZone;
          this.makeCompulsoryCalls();
        }
      });
  }


  makeCompulsoryCalls() {
    const requests = [
      this.getLevelOfService(),
      this.getSpaceTypeList(),
      this.getServiceAreas(),
    ];

    // Make all requests in parallel
    forkJoin(requests).subscribe(
      ([res1, res2, res3]) => {

        if (res1) {
          this.preLevelOfService = res1.reduce((obj, item) => {
            if (item.brokerId) {
              obj[item.brokerId] = item;
            }
            return obj;
          }, {});
        }
        if (res2) {
          this.preSpaceTypes = res2.reduce((obj, item) => {
            if (item.brokerId) {
              obj[item.brokerId] = item;
            }
            return obj;
          }, {});
        }
        
        if (res3) {
          this.preServiceAreas = res3.reduce((obj, item) => {
            if (item.brokerId) {
              obj[item.brokerId] = item;
            }
            return obj;
          }, {});
        }
        
        this.getMarketplaceTrips();

      },
      error => {
        sweetAlert('Error', error, 'error', 'OK');
      }
    );
  }

  getLevelOfService() {
    return this.levelOfServiceService.getLevelOfServices({ status: 'active' });
  }

  getSpaceTypeList() {
    return this.levelOfServiceService.getSpaceTypes({ status: 'active' });
  }

   getServiceAreas() {
    return this.serviceArea.getServiceAreas({ status: 'active' });
  }

  async getAuthData() {
    if (this.cachedAuthData) {
      return this.cachedAuthData;
    }

    const allSettings = await this.parserService.getBrokerSettingList().toPromise();
    if (allSettings && allSettings.length) {
      const settings = allSettings.find(broker => broker.name === 'awmt');
      if (settings) {
        const { username, password } = settings;
        const payload = {
          email: username,
          password,
          role: "company"
        };
        return await this.getBrokerToken(payload);
      }
    }
    return null;
  }

  async getMarketplaceTrips() {
    const authData = await this.getAuthData();
    if (authData && authData.accessToken) {
      const token = authData.accessToken;
      const queryParams: any = {
        limit: 9999,
        isFareEstimation: true,
        status: 'marketplace',
        time: [this.todayDate[0], this.todayDate[1]],
      }
      const res = await this.brokerService.getTrips(token, queryParams).toPromise();
      const { results } = res;
      if (results && results.length) {
        this.trips = results;
      }
    }
  }

  async getBrokerToken(payload) {
    let authData: any = { accessToken: '', companyId: '' };
    const response = await this.brokerService.getToken(payload).toPromise();
    const {tokens, user} = response;
    if (tokens && tokens.access && tokens.access.token) {
      authData.accessToken = tokens.access.token;
      if (user && user.company && user.company.id) {
        authData.companyId = user.company.id;
      }
      
      this.cachedAuthData = authData;
    } else {
      console.log('broker communication error');
    }
    return authData;
  }

  async AccepMtarketplaceTrips(trip: any) {
    try {
      const authData = await this.getAuthData();
      if (authData && authData.accessToken) {
        const acceptPayload = {
          pre: [],
          selectedIds: [trip._id],
          status: 'offered',
          company: authData.companyId
        };
        const response = await this.brokerService.AcceptMarketTrips(authData.accessToken, acceptPayload).toPromise();
        this.handleAcceptResponse(response, true);
      }
    } catch (error) {
      const response = error && error.error ? error.error : error;
      this.handleAcceptResponse(response, false, error.message);
    }
  }

  async reserveTrip(trip: any, isReserve: boolean = true) {
    const result = await sweetAlert(
      'Alert',
      `Are you sure you want to ${isReserve ? 'reserve' : 'unreserve'} this trip`,
      'warning',
      isReserve ? 'Reserve' : 'Unreserve',
      'Cancel'
    );
    if (!result.value) return;

    try {
      const authData = await this.getAuthData();
      if (authData && authData.accessToken) {
        const response = await this.brokerService.ReserveMarketTrip(authData.accessToken, trip._id, isReserve).toPromise();
        if (response) {
          sweetAlert('Success', `Trip successfully ${isReserve ? 'reserved' : 'unreserved'}.`, 'success', 'OK');
          this.getMarketplaceTrips();
        }
      }
    } catch (error) {
      const errorMessage = error && error.error && error.error.message ? error.error.message : (error.message || 'Failed to reserve trip');
      sweetAlert('Error', errorMessage, 'error', 'OK');
    }
  }

  isReserved(trip: any): boolean {
    const expiresAt = trip && trip.reserved && trip.reserved.expiresAt;
    if (!expiresAt) {
      return false;
    }
    return moment().isBefore(moment(expiresAt));
  }

  handleAcceptResponse(response: any, isSuccess: boolean, errorMessage?: string) {
    const skippedTrips = response && response.statusUpdate && response.statusUpdate.skippedTrips ? response.statusUpdate.skippedTrips : (response && response.skippedTripsIds ? response.skippedTripsIds : null);
    
    if (skippedTrips && skippedTrips.length > 0) {
      const html = this.getSkippedTripsHtml(skippedTrips);
      sweetAlertWithHtml('Alert', ' ', 'alert', 'OK', html);
      return;
    }

    if (isSuccess) {
      sweetAlert('Success', 'Trip successfully accepted.', 'success', 'OK');
      this.getMarketplaceTrips();
    } else {
      sweetAlert('Error', errorMessage || 'Failed to accept trip', 'error', 'OK');
    }
  }

    getSkippedTripsHtml(skippedTripsIds: any) {
      const skippedTrips = this.trips.filter((trip: any) => skippedTripsIds.includes(trip.id))
      const message = "This trip could not be updated due to fare/threshold issues.(<b>Please contact your broker</b>)";
      let html = `<p class="mb-4">${message}</p><div
      class="max-h-[400px] overflow-hidden p-2 flex flex-col rounded-lg bg-white dark:bg-zinc-950 border border-zinc-950/10 dark:border-white/10">
      <div class="h-full w-full overflow-hidden rounded-md ring-1 ring-zinc-950/10 dark:ring-white/10">
        <div class="flow-root h-full overflow-y-auto">
          <div class="-my-2">
            <div class="inline-block min-w-full py-2 align-middle">
              <table class="min-w-full border-separate border-spacing-0">
                <thead>
                  <tr>
                     <th
                      class="sticky top-0 z-10 px-3 py-1 text-left font-semibold bg-zinc-50 dark:bg-zinc-950 text-sm-dark border-b border-zinc-950/10 dark:border-white/10 text-nowrap">
                      Member
                    </th>
                    <th
                      class="sticky top-0 z-10 px-3 py-1 text-left font-semibold bg-zinc-50 dark:bg-zinc-950 text-sm-dark border-b border-zinc-950/10 dark:border-white/10 text-nowrap">
                      Reference ID
                    </th>
                  </tr>
                </thead>
                <tbody>`;
      skippedTrips.forEach((trip: any) => {
        html += `
                  <tr class="hover:bg-gray-950/5 dark:hover:bg-white/10">
                    <td class="px-3 py-1 text-left text-sm-light border-b border-zinc-950/10 dark:border-white/10 text-nowrap">
                     ${trip && trip.member && trip.member.displayName ? new TitleCasePipe().transform(trip.member.displayName) : ''}
                    </td>
                    <td class="px-3 py-1 text-left text-sm-light border-b border-zinc-950/10 dark:border-white/10 text-nowrap">
                     ${trip.reference}${trip.suffix ? '-'+trip.suffix : ''}
                    </td>
                  </tr>
                  `;
      });
      html += `
                </tbody>
              </table>
              </div>
              </div>
              </div>
              </div>
              </div>
              `
      return html;
    }

  ngOnDestroy(): void {
  }
}
